import { create } from "zustand";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import CloseIcon from '@mui/icons-material/Close';
import CSSTransition from "react-transition-group/CSSTransition";
import { useTimer } from "../hooks/use-timer";
import { Box, Button, css, styled } from "@mui/material";

export const ToastsContainer = styled(Box)`
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: ${({theme}) => theme.zIndex.modal};
`;

export const StledToastProgressBar = styled(Box)`
  background-color: currentColor;
  height: 2px;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0.6;
`;

export const StyledToastItemContent = styled(Box)`
  padding: 8px 12px;
  display: flex;
  position: relative;
  box-shadow: 0 0 10px rgba(0,0,0,0.2), 0 6px 10px -6px rgba(0,0,0,0.6);
  border-radius: 0.365em;
  overflow: hidden;
  > div {
    margin-right: 8px;
    > p {
      margin-top: 0;
    }
  }
`;

export const StyledToastItemClose = styled(Button)`
  color: currentColor;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-size: 0;
  margin-right: -4px;
  
  > svg {
    font-size: 22px;
  }
`;

export const StyledToastItem = styled(Box, {
  shouldForwardProp: (prop) => ![
    'dismissible',
    'timeout',
    '$locked',
    '$type',
    '$interact',
    '$content',
  ].includes(prop),
})`
  margin: 16px;
  display: flex;
  // position: absolute;
  // top: 0;
  // left: 0;

  ${(props) => {
    let color = props.theme.palette.toast[props.$type];
    // let bright = props.theme.lum(color) > 125;
    return css`
      > div {
        background-color: ${color};
        color: black;
      }
    `;
  }}
`;


let globalToastID = 0; // global toast id counter
const useToast = create((set, get) => ({
  items: [],
  pushRef: (id, ref) => { 
    const item = get().items.filter(item => item.id === id)[0];
    const items = get().items.filter(item => item.id !== id);
    item.ref = ref;
    item.index = items.length;
    items.push(item);
    set({ items });
  },
  add: (item) => {
    globalToastID++;
    set(state => ({ 
      items: [
        ...state.items,
        { id: globalToastID, ...item }
      ] 
    }));  
  },
  remove: id => {
    set(state => ({ items: state.items.filter(item => item.id !== id) }));
  }
}));

/**
 * addToast
 * @param {mixex} content toast content
 * @param {ToastProps} settings item settings
 */
export const addToast = (content, settings) => {
  useToast.getState().add(
    Object.assign({
      onClose: () => {},
      dismissible: true,
      timeout: 5000, // ms
      $locked: false,
      $type: 'info',
      $interact: true,
      $content: content
    }, settings)
  );
};

/**
 * @param {ToastProps} props
 */
const ToastItem = memo((props) => {
  // const items = useToast(state => state.items.filter(item => item.id !== props.id));
  // const pushRef = useToast(state => state.pushRef);
  const remove = useToast(state => state.remove);
  const [shown, setShown] = useState(false);
  const mounted = useRef();
  const progressBar = useRef();
  const item = useRef();
  const styles = useRef(); 
  const timer = useRef(
    useTimer( 
      () => {
        // console.log('animation end');
        setShown(false);
      },
      props.timeout
    )
  );

  const removeMe = useCallback(
    () => {
      mounted.current = false;
      remove(props.id);
      timer.current.stop();
    },
    [remove, props.id]
  );

  useEffect(
    () => {
      if(shown && !timer.current.running && !props.$locked) {
        timer.current.start((animProgress) => {
          const width = `${(100 - (animProgress * 100)).toFixed(2)}%`;
          progressBar.current.style.width = width;
        });
      }
    },
    [shown, props.$locked]
  );

  // constructor
  useEffect(
    () => {
      setShown(true);
      mounted.current = true;
      const currentTimer = timer.current;
      return () => {
        // During cleanup phase i want to remove this
        // notification only if it is mounted and animation is running.
        // This happens if the app gets unmounted.
        // Otherwise <CSSTransition /> will take care
        // of this job.
        // This sure is dirty.
        if(mounted.current && currentTimer.running) {
          removeMe();
        }
      }
    },
    [removeMe]
  );

  const onMouseEnter = (event) => {
    if(props.$interact) {
      timer.current.cancel();
    }
  };

  const onMouseLeave = (event) => {
    if(props.$interact && !props.$locked) {
      timer.current.enqueue();
    }
  };

  return (
    <CSSTransition
      in={shown}
      timeout={250}
      classNames={'blur-in'}
      unmountOnExit
      // onEntering={setPosition}
      onExited={removeMe}
    >
      <StyledToastItem 
        {...props}
        ref={item}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <StyledToastItemContent style={styles.current}>
          {!props.$locked && (
            <StledToastProgressBar ref={progressBar} />
          )}
          <div>{props.$content}</div>
          {props.dismissible && (
            <StyledToastItemClose onClick={() => setShown(false)}>
              <CloseIcon />
            </StyledToastItemClose>
          )}
        </StyledToastItemContent>
      </StyledToastItem>
    </CSSTransition>
  );
});

export const Toasts = memo((props) => {
  const items = useToast(state => state.items);
  // if(!props?.root?.current) {
  //   return null;
  // }
  // return createPortal(
  //   <ToastsContainer>
  //     {items.map(
  //       (item) => <ToastItem key={item.id} {...item} />
  //     )}
  //   </ToastsContainer>,
  //   props.root.current
  // );
  return (
    <ToastsContainer>
      {items.map(
        (item) => <ToastItem key={item.id} {...item} />
      )}
    </ToastsContainer>
  );
});